import React, { useMemo } from 'react';
import PropTypes from "prop-types";
import { useSelector } from 'react-redux';
import { noop } from "rhombus-react";

import { getConnectedPlatforms } from "selectors/Social2";

import { ADTYPE_LABELS } from "social/constants/common";
import { adTypeSort } from 'social/utils';
import SocialAdTypeSelectionCard from 'social/SocialAdTypeSelectionCard';

import { SOCIAL_AD_TYPES, PLATFORMS } from 'utils/constants';

import "./SocialAdTypeSelector.css";

const PLATFORM_FORMATS = {
	[PLATFORMS.FACEBOOK]: {
		platform: PLATFORMS.FACEBOOK,
		icon: "fab fa-meta facebook-logo",
		adTypes: [
			SOCIAL_AD_TYPES.FACEBOOK_IMAGE,
			SOCIAL_AD_TYPES.FACEBOOK_VIDEO,
			SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL,
		],
	},
	[PLATFORMS.FACEBOOK_MO]: {
		platform: "Meta",
		icon: "fab fa-meta facebook-logo",
		adTypes: [
			SOCIAL_AD_TYPES.FACEBOOK_IMAGE_MO,
			SOCIAL_AD_TYPES.FACEBOOK_VIDEO_MO,
			SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL_MO,
		],
	},
	[PLATFORMS.GOOGLE]: {
		platform: PLATFORMS.GOOGLE,
		icon: (<img className="logo" src="/images/GoogleDiscovery.png" />),
		adTypes: [
			SOCIAL_AD_TYPES.GOOGLE_DEMAND_GEN_IMAGE,
			SOCIAL_AD_TYPES.GOOGLE_DEMAND_GEN_CAROUSEL,
		],
	},
	[PLATFORMS.LINKEDIN]: {
		platform: PLATFORMS.LINKEDIN,
		icon: "fab fa-linkedin",
		adTypes: [
			SOCIAL_AD_TYPES.LINKEDIN_IMAGE,
			SOCIAL_AD_TYPES.LINKEDIN_VIDEO,
			SOCIAL_AD_TYPES.LINKEDIN_CAROUSEL,
		],
	},
	[PLATFORMS.SNAPCHAT]: {
		platform: PLATFORMS.SNAPCHAT,
		icon: "fab fa-snapchat",
		adTypes: [
			SOCIAL_AD_TYPES.SNAPCHAT_IMAGE,
			SOCIAL_AD_TYPES.SNAPCHAT_VIDEO,
			SOCIAL_AD_TYPES.SNAPCHAT_COMMERCIAL,
			SOCIAL_AD_TYPES.SNAPCHAT_CAROUSEL,
		],
	},
	[PLATFORMS.PINTEREST]: {
		platform: PLATFORMS.PINTEREST,
		icon: "fab fa-pinterest",
		adTypes: [
			SOCIAL_AD_TYPES.PINTEREST_IMAGE,
			SOCIAL_AD_TYPES.PINTEREST_VIDEO,
			SOCIAL_AD_TYPES.PINTEREST_MAX_WIDTH_VIDEO,
			SOCIAL_AD_TYPES.PINTEREST_CAROUSEL,
		],
	},
	[PLATFORMS.TWITTER]: {
		platform: PLATFORMS.TWITTER,
		icon: "fab fa-x-twitter x-twitter-logo",
		adTypes: [
			SOCIAL_AD_TYPES.TWITTER_IMAGE,
			SOCIAL_AD_TYPES.TWITTER_VIDEO,
		],
	},
	[PLATFORMS.YOUTUBE]: {
		platform: PLATFORMS.YOUTUBE,
		icon: "fab fa-youtube",
		adTypes: [
			SOCIAL_AD_TYPES.YOUTUBE_INSTREAM,
		],
	},
	[PLATFORMS.TIKTOK]: {
		platform: PLATFORMS.TIKTOK,
		icon: "fab fa-tiktok tiktok-logo",
		adTypes: [
			SOCIAL_AD_TYPES.TIKTOK_VIDEO,
		],
	},
	[PLATFORMS.REDDIT]: {
		platform: PLATFORMS.REDDIT,
		icon: "fab fa-reddit",
		adTypes: [
			SOCIAL_AD_TYPES.REDDIT_IMAGE,
			SOCIAL_AD_TYPES.REDDIT_VIDEO,
		],
	},
};

export default function SocialAdTypeSelector(props){
	const {
		input: { onChange = noop, value },
		disabledAdTypes,
	} = props;

	const { connectedPlatforms } = useSelector(state => ({
		connectedPlatforms: getConnectedPlatforms(state),
	}));

	const selectedAdTypes = new Set(value);

	const { platformCards, availableAdTypes } = useMemo(() => {
		const availableAdTypes = new Set();
		const disabledAdTypesSet = new Set(disabledAdTypes);

		const platformCards = connectedPlatforms.reduce((prev, connection) => {
			const format = PLATFORM_FORMATS[connection];
			const card = {
				...format,
				options: [],
			};

			if (!card.platform) { return prev; }

			card.adTypes.forEach((adType) => {
				const qaSelector = `cypress-${adType}-ad-type-selection`;

				const option = {
					label: ADTYPE_LABELS[adType],
					value: adType,
					className: qaSelector,
					isSelected: false,
					disabled: false,
				};

				if (disabledAdTypesSet.has(option.value)) {
					option.disabled = true;
					option.isSelected = true;
				} else {
					availableAdTypes.add(option.value);
					option.isSelected = selectedAdTypes.has(option.value);
				}

				card.options.push(option);
			});

			delete card.adTypes;

			prev.push(card);
			return prev;
		}, []).sort(adTypeSort);

		return { platformCards, availableAdTypes };
	}, [connectedPlatforms, value, disabledAdTypes]);

	function updateSelection(values, isSelected) {
		const newSelections = new Set(value);

		if (isSelected) {
			values.forEach(option => newSelections.add(option));
		} else {
			values.forEach(option => newSelections.delete(option));
		}

		onChange([...newSelections]);
	}

	function handleSelectAllFormats() {
		if (selectedAdTypes.size === availableAdTypes.size) {
			onChange([]);
		} else {
			onChange([...availableAdTypes]);
		}
	}

	return (
		<div className="social-adtype-selector">
			<div className="e-form__control-wrapper">
				<label className="e-form__control-label">
					Select Platform Formats
				</label>
				<label className="e-form__checkbox select-all-formats">
					<input
						type="checkbox"
						name="Select All Formats"
						className="custom-checkbox"
						checked={selectedAdTypes.size === availableAdTypes.size}
						onChange={handleSelectAllFormats}
					/>
					<span className="e-form__checkbox--bg" />
					<span className="e-form__checkbox--label">Select All Formats</span>
				</label>
			</div>
			<div className="cards-container">
				{platformCards.map(card => (
					<SocialAdTypeSelectionCard
						key={card.platform}
						{...card}
						onSelect={updateSelection}
					/>
				))}
			</div>
		</div>
	);
}

SocialAdTypeSelector.displayName = "SocialAdTypeSelector";

SocialAdTypeSelector.propTypes = {
	input: PropTypes.object,
	meta: PropTypes.object,
	disabledAdTypes: PropTypes.arrayOf(PropTypes.string),
	onChange: PropTypes.func,
};

SocialAdTypeSelector.defaultProps = {
	input: {
		onChange: noop,
	},
	meta: {},
	disabledAdTypes: [],
	onChange: noop,
};
